import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Subscription } from "rxjs";
import { PushService } from "src/app/core/services/push.service";
import { ToastService } from "src/app/core/services/toast.service";

/**
 * unsupported   : navigateur sans Web Push (ou service worker inactif, ex. ng serve)
 * ios-install   : iPhone / iPad, application pas encore installée sur l'écran d'accueil
 * denied        : permission refusée
 * enabled       : permission accordée et ce navigateur est abonné
 * available     : on peut proposer « Activer les notifications »
 */
type PushState = "unsupported" | "ios-install" | "denied" | "enabled" | "available";

/** Bloc « Notifications » (page Mon profil). La permission n'est demandée qu'au clic sur « Activer ». */
@Component({
	selector: "app-push-notifications",
	templateUrl: "./push-notifications.component.html",
})
export class PushNotificationsComponent implements OnInit, OnDestroy {
	/** false : sans carte ni titre, pour un onglet déjà encadré (Modifier le profil). */
	@Input() enCarte = true;

	protected state: PushState = "unsupported";
	protected isSubscribing = false;
	protected isUnsubscribing = false;
	protected isTesting = false;

	private subscribed = false;
	private subscription?: Subscription;

	constructor(
		private pushService: PushService,
		private toastService: ToastService
	) {}

	ngOnInit(): void {
		this.refresh();
		this.pushService.resync();
		this.subscription = this.pushService.subscription$.subscribe((sub) => {
			this.subscribed = !!sub;
			this.refresh();
		});
	}

	ngOnDestroy(): void {
		this.subscription?.unsubscribe();
	}

	async activer(): Promise<void> {
		this.isSubscribing = true;
		try {
			await this.pushService.subscribe();
			this.toastService.success("Notifications activées sur cet appareil.");
		} catch (error: any) {
			this.toastService.error(error?.message || "Impossible d'activer les notifications.");
		} finally {
			this.isSubscribing = false;
			this.refresh();
		}
	}

	async desactiver(): Promise<void> {
		this.isUnsubscribing = true;
		await this.pushService.unsubscribe();
		this.isUnsubscribing = false;
		this.toastService.success("Notifications désactivées sur cet appareil.");
		this.refresh();
	}

	async envoyerTest(): Promise<void> {
		this.isTesting = true;
		try {
			const result = await this.pushService.sendTest();
			if (result.sent) this.toastService.success(`Notification de test envoyée (${result.sent} appareil${result.sent > 1 ? "s" : ""}).`);
			else this.toastService.error("La notification de test n'a pas pu être envoyée.");
		} catch (error: any) {
			this.toastService.error(error?.error?.error?.message || "La notification de test n'a pas pu être envoyée.");
		} finally {
			this.isTesting = false;
		}
	}

	private refresh(): void {
		// Safari iOS hors écran d'accueil n'expose pas PushManager : le guide d'installation passe avant « non compatible »
		if (this.pushService.isIOS() && !this.pushService.isInstalled()) this.state = "ios-install";
		else if (!this.pushService.isSupported()) this.state = "unsupported";
		else if (this.pushService.permission() === "denied") this.state = "denied";
		else if (this.pushService.permission() === "granted" && this.subscribed) this.state = "enabled";
		else this.state = "available";
	}
}
